<template>
  <Breadcrumb :items="breadcrumbs" />
  <Loading v-if="store.loading" />

  <div v-else class="ds-page">
    <div class="ds-container ds-container--wide">
      <div class="ds-page-header">
        <div>
          <h1 class="ds-page-header__title">الكورسات</h1>
          <p class="ds-page-header__subtitle">إدارة جميع كورسات المنصة، الأسعار وإعدادات الظهور</p>
        </div>
        <div class="ds-page-header__actions">
          <router-link to="/courses/create" class="ds-btn ds-btn--primary">
            <i class="pi pi-plus"></i> إضافة كورس
          </router-link>
        </div>
      </div>

      <!-- Empty state -->
      <div v-if="!store.courses.length" class="empty-state empty-state--card">
        <div class="empty-state__icon"><i class="pi pi-inbox"></i></div>
        <h3 class="empty-state__title">لا توجد كورسات</h3>
        <p class="empty-state__desc">ابدأ بإضافة أول كورس ليظهر هنا ويمكن إدارة أسعاره وإعداداته.</p>
        <router-link to="/courses/create" class="ds-btn ds-btn--primary empty-state__action">
          <i class="pi pi-plus"></i> إضافة كورس
        </router-link>
      </div>

      <template v-else>
        <!-- Desktop / tablet table -->
        <div class="ds-table-wrap table-responsive">
          <table class="table align-middle mb-0">
            <thead>
              <tr>
                <th class="ps-3">الكورس</th>
                <th class="text-center">سعر الكورس <small class="text-muted">(مدى الحياة)</small></th>
                <th class="text-center">التحكم</th>
                <th class="text-center">الحالة</th>
                <th class="text-center pe-3">إجراءات</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="course in store.courses" :key="course.id">
                <td class="ps-3">
                  <router-link :to="`/courses/${course.id}`" class="course-cell">
                    <img v-if="course.image_link" :src="course.image_link" class="course-thumb" alt="" />
                    <span v-else class="course-thumb course-thumb--empty"><i class="pi pi-book"></i></span>
                    <div class="course-meta">
                      <div class="course-name">{{ course.title }}</div>
                      <span v-if="course.academic_year" class="badge-pill">
                        <i class="pi pi-graduation-cap"></i> {{ course.academic_year.name }}
                      </span>
                    </div>
                  </router-link>
                </td>
                <td class="text-center"><PriceBadge :value="course.price" /></td>
                <td>
                  <div class="chip-row justify-content-center">
                    <button
                      class="chip-toggle" :class="course.is_visible ? 'chip-toggle--on' : 'chip-toggle--off'"
                      v-tooltip.top="course.is_visible ? 'اضغط لإخفاء الكورس' : 'اضغط لإظهار الكورس'"
                      @click="toggleVisible(course)"
                    >
                      <i :class="course.is_visible ? 'pi pi-eye' : 'pi pi-eye-slash'"></i> ظاهر
                    </button>
                    <button
                      class="chip-toggle" :class="course.show_on_home ? 'chip-toggle--home' : 'chip-toggle--off'"
                      v-tooltip.top="'الظهور في الصفحة الرئيسية بالموقع'"
                      @click="toggleHome(course)"
                    >
                      <i class="pi pi-home"></i> الرئيسية
                    </button>
                    <button
                      class="chip-toggle" :class="course.is_subscription_enabled ? 'chip-toggle--on' : 'chip-toggle--off'"
                      v-tooltip.top="'تفعيل/تعطيل الاشتراك المدفوع'"
                      @click="toggleSubscription(course)"
                    >
                      <i class="pi pi-lock"></i> اشتراك
                    </button>
                  </div>
                </td>
                <td class="text-center">
                  <span class="status-badge" :class="statusOf(course).cls">{{ statusOf(course).label }}</span>
                </td>
                <td class="text-center pe-3">
                  <div class="icon-action-group justify-content-center">
                    <router-link :to="`/courses/${course.id}`" class="icon-action-btn icon-action-btn--edit" v-tooltip.top="'فتح الكورس'">
                      <i class="pi pi-pencil"></i>
                    </router-link>
                    <button class="icon-action-btn icon-action-btn--danger" v-tooltip.top="'حذف الكورس'" @click="remove(course)">
                      <i class="pi pi-trash"></i>
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Mobile card list (no horizontal scroll) -->
        <div class="mobile-list">
          <div v-for="course in store.courses" :key="course.id" class="mobile-list-card">
            <div class="mobile-list-card__top">
              <img v-if="course.image_link" :src="course.image_link" class="mobile-list-card__thumb" alt="" />
              <span v-else class="mobile-list-card__thumb"><i class="pi pi-book"></i></span>
              <div class="mobile-list-card__meta">
                <div class="mobile-list-card__title">{{ course.title }}</div>
                <span v-if="course.academic_year" class="badge-pill"><i class="pi pi-graduation-cap"></i> {{ course.academic_year.name }}</span>
              </div>
              <span class="status-badge" :class="statusOf(course).cls">{{ statusOf(course).label }}</span>
            </div>
            <div class="mobile-list-card__facts">
              <span>سعر الكورس (مدى الحياة): <b><PriceBadge :value="course.price" /></b></span>
            </div>
            <div class="chip-row">
              <button class="chip-toggle" :class="course.is_visible ? 'chip-toggle--on' : 'chip-toggle--off'" @click="toggleVisible(course)">
                <i :class="course.is_visible ? 'pi pi-eye' : 'pi pi-eye-slash'"></i> ظاهر
              </button>
              <button class="chip-toggle" :class="course.show_on_home ? 'chip-toggle--home' : 'chip-toggle--off'" @click="toggleHome(course)">
                <i class="pi pi-home"></i> الرئيسية
              </button>
              <button class="chip-toggle" :class="course.is_subscription_enabled ? 'chip-toggle--on' : 'chip-toggle--off'" @click="toggleSubscription(course)">
                <i class="pi pi-lock"></i> اشتراك
              </button>
            </div>
            <div class="mobile-list-card__actions">
              <router-link :to="`/courses/${course.id}`" class="ds-btn ds-btn--secondary ds-btn--sm">
                <i class="pi pi-pencil"></i> فتح الكورس
              </router-link>
              <button class="ds-btn ds-btn--secondary ds-btn--sm" @click="remove(course)">
                <i class="pi pi-trash"></i>
              </button>
            </div>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import Swal from 'sweetalert2';
import Breadcrumb from '@/components/global/breadcrumb.vue';
import Loading from '@/components/global/loading.vue';
import PriceBadge from './PriceBadge.vue';
import { useCourseStore } from '@/stores/useCourseStore.js';
import { apiError } from '@/composables/subscriptionMeta.js';

export default {
  name: 'CoursesIndex',
  components: { Breadcrumb, Loading, PriceBadge },
  data() {
    return {
      breadcrumbs: [{ name: 'الرئيسية', path: '/' }, { name: 'الكورسات', path: '/courses' }],
    };
  },
  computed: {
    store() { return useCourseStore(); },
  },
  methods: {
    hasAnyPrice(c) {
      // A course is priced when its single lifetime price is set.
      return c.price !== null && c.price !== undefined && c.price !== '';
    },
    statusOf(course) {
      if (course.is_subscription_enabled && this.hasAnyPrice(course)) {
        return { label: 'متاح للاشتراك', cls: 'status-badge--accepted' };
      }
      if (course.is_subscription_enabled) {
        return { label: 'بدون أسعار', cls: 'status-badge--pending' };
      }
      return { label: 'غير مُفعّل', cls: 'status-badge--blocked' };
    },
    async toggleVisible(course) {
      try { await this.store.toggleVisible(course.id); }
      catch (e) { Swal.fire({ icon: 'error', title: apiError(e), timer: 2000, showConfirmButton: false }); }
    },
    async toggleSubscription(course) {
      try { await this.store.toggleSubscription(course.id); }
      catch (e) { Swal.fire({ icon: 'error', title: apiError(e), timer: 2000, showConfirmButton: false }); }
    },
    async toggleHome(course) {
      try { await this.store.toggleHome(course.id); }
      catch (e) { Swal.fire({ icon: 'error', title: apiError(e), timer: 2000, showConfirmButton: false }); }
    },
    async remove(course) {
      const res = await Swal.fire({
        title: 'حذف الكورس', html: `سيتم حذف <strong>${course.title}</strong> نهائياً.`,
        icon: 'warning', showCancelButton: true, confirmButtonText: 'نعم، احذف',
        cancelButtonText: 'إلغاء', confirmButtonColor: '#dc3545',
      });
      if (!res.isConfirmed) return;
      try {
        await this.store.deleteCourse(course.id);
        Swal.fire({ icon: 'success', title: 'تم الحذف', timer: 1200, showConfirmButton: false });
      } catch (e) {
        Swal.fire({ icon: 'error', title: apiError(e), timer: 2500, showConfirmButton: false });
      }
    },
  },
  mounted() {
    this.store.fetchCourses();
  },
};
</script>

<style scoped>
.course-cell { display: flex; align-items: center; gap: 0.75rem; text-decoration: none; color: inherit; }
.course-meta { display: flex; flex-direction: column; gap: 0.3rem; min-width: 0; }
.course-thumb {
  width: 44px; height: 44px; border-radius: var(--radius-md); object-fit: cover; display: inline-flex;
  align-items: center; justify-content: center; background: var(--primary-light); color: var(--primary-color);
  border: 1px solid var(--surface-border); font-size: 1.0625rem; flex-shrink: 0;
}
.course-name { font-weight: 700; color: var(--text-primary); font-size: 0.875rem; }
</style>
